/**
 * Extract the runtime theme tokens for embedded apps.
 *
 * Mirrors the backend `ThemeTokens` shape (`functions/src/services/apps/theme-tokens.ts`)
 * but reads live values from the document's CSS custom properties so admin
 * theme overrides (set on `:root.style` by `PublicLayout`) are reflected.
 *
 * The JWT theme claim is a snapshot at session-issue time; this helper backs
 * the `rumio:design` postMessage that propagates live theme changes to
 * embedded apps without requiring a session refresh.
 */

export interface ThemeTokens {
  primary: Record<string, string>;
  secondary: Record<string, string>;
  accent: { base: string; light: string; dark: string };
  warm: { cream: string; linen: string; parchment: string };
  text: { primary: string; secondary: string; muted: string };
  bg: { primary: string; secondary: string; tertiary: string; warm: string };
  fonts: { sans: string; display: string };
  dark_mode: boolean;
  focus_ring: string;
}

const PALETTE_STEPS = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900"] as const;

function readVar(styles: CSSStyleDeclaration, name: string): string {
  return styles.getPropertyValue(name).trim();
}

function readPalette(styles: CSSStyleDeclaration, prefix: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const step of PALETTE_STEPS) {
    const v = readVar(styles, `--color-${prefix}-${step}`);
    if (v) out[step] = v;
  }
  return out;
}

export function extractThemeTokens(doc: Document = document): ThemeTokens {
  const styles = getComputedStyle(doc.documentElement);
  return {
    primary: readPalette(styles, "sage"),
    secondary: readPalette(styles, "brown"),
    accent: {
      base: readVar(styles, "--color-terra"),
      light: readVar(styles, "--color-terra-light"),
      dark: readVar(styles, "--color-terra-dark"),
    },
    warm: {
      cream: readVar(styles, "--color-cream"),
      linen: readVar(styles, "--color-linen"),
      parchment: readVar(styles, "--color-parchment"),
    },
    text: {
      primary: readVar(styles, "--text-primary"),
      secondary: readVar(styles, "--text-secondary"),
      muted: readVar(styles, "--text-muted"),
    },
    bg: {
      primary: readVar(styles, "--bg-primary"),
      secondary: readVar(styles, "--bg-secondary"),
      tertiary: readVar(styles, "--bg-tertiary"),
      warm: readVar(styles, "--bg-warm"),
    },
    fonts: {
      sans: readVar(styles, "--font-sans"),
      display: readVar(styles, "--font-display"),
    },
    dark_mode: doc.documentElement.classList.contains("dark"),
    focus_ring: readVar(styles, "--focus-ring"),
  };
}
